<script>
    import { selectedItem } from '../../store/store.js';
    import { tooltip } from '../../utils/utils.js';
    import './shared.css';
</script>

{#if $selectedItem?.stadgar_id !== null}
<div class="stadgar-section">
    <div class="stadgar-content">
        <div class="info-box">
            <p class="info-box-text">
                <strong>Viktig Information Från Stadgarna</strong>
            </p>
        </div>

        <div class="sections-container">
            <div class="section">
                <div class="section-header">
                    <i class="fa-solid fa-key section-icon"></i>
                    <h3>Sektion 1: Ägande & Medlemskap</h3>
                </div>
                <p class="section-description">Här fokuserar vi på vem som får köpa och bo i lägenheten.</p>

                <div class="items-grid">
                    {#if $selectedItem?.membership?.juridisk_person_is_allowed !== null && $selectedItem?.membership?.juridisk_person_is_allowed !== undefined}
                        <div class="item-card">
                            <div class="item-header">
                                <span class="item-title">Juridisk person</span>
                            </div>
                            <div class="item-status status-{$selectedItem.membership.juridisk_person_is_allowed ? 'allowed' : 'not-allowed'}">
                                {$selectedItem.membership.juridisk_person_is_allowed ? "Tillåts" : "Tillåts ej"}
                            </div>
                            <p class="item-explanation">
                                Viktigt att veta: Att föreningen godkänner juridisk person (företag) är ovanligt. Det kan vara positivt för företagare, men om för stor andel av föreningen ägs av företag kan föreningen klassas som "oäkta", vilket förändrar skattereglerna för dig som privatperson.
                            </p>
                        </div>
                    {/if}

                    {#if $selectedItem?.membership?.shared_ownership !== null && $selectedItem?.membership?.shared_ownership !== undefined}
                        <div class="item-card">
                            <div class="item-header">
                                <span class="item-title">Delat ägande</span>
                            </div>
                            <div class="item-status status-{$selectedItem.membership.shared_ownership ? 'allowed' : 'not-allowed'}">
                                {$selectedItem.membership.shared_ownership ? "Tillåts" : "Tillåts ej"}
                            </div>
                            <p class="item-explanation">
                                Delat ägande av bostadsrätter är ett upplägg som ofta används när föräldrar hjälper sina barn med bostadsköp men som föreningen har rätt att neka om inte alla ägare bosätter sig i lägenheten. Rekommendationen är dock att tillåta detta eftersom alternativet ofta blir en mer administrativt krävande andrahandsuthyrning, och vid delat ägande är samtliga parter fullt ansvariga för föreningens avgifter och regler.
                            </p>
                        </div>
                    {/if}

                    {#if $selectedItem?.rights_and_obligations?.subletting_rules}
                        <div class="item-card">
                            <div class="item-header">
                                <span class="item-title">Andrahandsuthyrning</span>
                            </div>
                            <div class="item-status status-{$selectedItem.rights_and_obligations.subletting_rules.permission_required ? 'requires-permission' : 'allowed'}">
                                {$selectedItem.rights_and_obligations.subletting_rules.permission_required ? "Tillstånd krävs" : "Tillåts"}
                            </div>
                            <p class="item-explanation">
                                Andrahandsuthyrning innebär att du låter någon annan använda din lägenhet självständigt när du inte själv bor där. Detta kräver alltid styrelsens tillstånd och ett giltigt skäl, exempelvis studier på annan ort eller provsamboende. Du är fortsatt fullt ansvarig för avgifter och skötsel under tiden, och hyr du ut utan lov riskerar du att bli av med bostadsrätten.
                            </p>
                            {#if $selectedItem.rights_and_obligations.subletting_rules.time_limits}
                                <div class="item-details">
                                    <strong>Tidsbegränsning:</strong> {$selectedItem.rights_and_obligations.subletting_rules.time_limits}
                                </div>
                            {/if}
                        </div>
                    {/if}
                </div>
            </div>

            <div class="section">
                <div class="section-header">
                    <i class="fa-solid fa-wallet section-icon"></i>
                    <h3>Sektion 2: Ekonomi & Avgifter</h3>
                </div>

                <div class="economy-grid">
                    {#if $selectedItem?.financial_obligations?.annual_fee}
                        {#if $selectedItem.financial_obligations.annual_fee.payment_schedule}
                            <div class="item-card">
                                <div class="item-header">
                                    <span class="item-title">Betalningsschema</span>
                                </div>
                                <p class="item-explanation">
                                    {$selectedItem.financial_obligations.annual_fee.payment_schedule}
                                </p>
                                <p class="item-explanation" style="margin-top: auto; padding-top: 0.5rem; border-top: 1px solid rgba(0, 0, 0, 0.08); font-size: 0.85rem; color: #6c757d;">
                                    Avgiften täcker föreningens gemensamma kostnader (räntor, drift, yttre underhåll). Det är mycket viktigt att betala i tid eftersom sena betalningar enligt bostadsrättslagen kan leda till att nyttjanderätten förverkas (att du tvingas flytta).
                                </p>
                            </div>
                        {/if}
                        {#if $selectedItem.financial_obligations.annual_fee.calculation_method}
                            <div class="item-card">
                                <div class="item-header">
                                    <span class="item-title">Beräkningsmetod</span>
                                </div>
                                <p class="item-explanation">
                                    {$selectedItem.financial_obligations.annual_fee.calculation_method}
                                </p>
                                <p class="item-explanation" style="margin-top: auto; padding-top: 0.5rem; border-top: 1px solid rgba(0, 0, 0, 0.08); font-size: 0.85rem; color: #6c757d;">
                                    Hur årsavgiften fördelas mellan lägenheterna. Andelstalet är ett fast värde som speglar just din lägenhets ekonomiska andel av föreningens totala tillgångar och skulder, ofta baserat på ursprunglig insats och standard.
                                </p>
                            </div>
                        {/if}
                    {/if}
                </div>
            </div>

            <div class="section">
                <div class="section-header">
                    <i class="fa-solid fa-hammer section-icon"></i>
                    <h3>Sektion 3: Din Lägenhet (Underhåll & Renovering)</h3>
                </div>
                <p class="section-description">Gränsdragningen mellan ditt och föreningens ansvar.</p>

                <div class="items-grid">
                    {#if $selectedItem?.rights_and_obligations?.property_modifications}
                        {#if $selectedItem.rights_and_obligations.property_modifications.permission_requirements || $selectedItem.rights_and_obligations.property_modifications.restricted_actions}
                            <div class="item-card">
                                <div class="item-header">
                                    <span class="item-title">Renovering</span>
                                </div>
                                <div class="item-status status-requires-permission">Kräver tillstånd</div>
                                {#if $selectedItem.rights_and_obligations.property_modifications.permission_requirements}
                                    <p class="item-explanation">
                                        {$selectedItem.rights_and_obligations.property_modifications.permission_requirements}
                                    </p>
                                {/if}
                                {#if $selectedItem.rights_and_obligations.property_modifications.restricted_actions}
                                    <div class="item-details">
                                        {$selectedItem.rights_and_obligations.property_modifications.restricted_actions}
                                    </div>
                                {/if}
                            </div>
                        {/if}
                    {/if}

                    {#if $selectedItem?.rights_and_obligations?.maintenance_responsibilities}
                        {#if $selectedItem.rights_and_obligations.maintenance_responsibilities.member_responsibilities}
                            <div class="item-card">
                                <div class="item-header">
                                    <span class="item-title">Underhållsansvar</span>
                                </div>
                                <div class="item-status status-allowed">Du (Inre underhåll)</div>
                                <p class="item-explanation">
                                    {$selectedItem.rights_and_obligations.maintenance_responsibilities.member_responsibilities}
                                </p>
                            </div>
                        {/if}
                        {#if $selectedItem.rights_and_obligations.maintenance_responsibilities.association_responsibilities}
                            <div class="item-card">
                                <div class="item-header">
                                    <span class="item-title">Underhållsansvar</span>
                                </div>
                                <div class="item-status status-association">Föreningen (Yttre underhåll)</div>
                                <p class="item-explanation">
                                    {$selectedItem.rights_and_obligations.maintenance_responsibilities.association_responsibilities}
                                </p>
                            </div>
                        {/if}
                    {/if}
                </div>
            </div>
        </div>
    </div>
</div>
{/if}

<style>
    .stadgar-section {
        width: 100%;
    }

    .stadgar-content {
        background: white;
        backdrop-filter: blur(30px) saturate(120%);
        -webkit-backdrop-filter: blur(30px) saturate(120%);
        border-radius: 24px;
        border: none;
        padding: 2rem;
        box-shadow:
            0 8px 32px rgba(0, 0, 0, 0.06),
            inset 0 1px 0 rgba(255, 255, 255, 0.3),
            inset 0 -1px 0 rgba(255, 255, 255, 0.1),
            0 0 0 1px rgba(255, 255, 255, 0.15);
    }

    .info-box {
        background: linear-gradient(135deg, rgba(66, 165, 245, 0.1) 0%, rgba(33, 150, 243, 0.05) 100%);
        border-left: 4px solid #42a5f5;
        border-radius: 12px;
        padding: 1rem 1.5rem;
        margin-bottom: 2rem;
    }

    .info-box-text {
        margin: 0;
        color: #495057;
        line-height: 1.6;
        font-size: 0.95rem;
    }

    .info-box-text strong {
        color: #1976d2;
        font-weight: 600;
    }

    .sections-container {
        display: flex;
        flex-direction: column;
        gap: 2.5rem;
    }

    .section {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .section-header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-bottom: 0.5rem;
    }

    .section-icon {
        font-size: 1.5rem;
        color: #4361ee;
    }

    .section-header h3 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 600;
        color: #2c3e50;
    }

    .section-description {
        margin: 0 0 1rem 0;
        color: #6c757d;
        font-size: 0.9rem;
        font-style: italic;
        text-align: left;
    }

    .items-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 1.25rem;
    }

    .economy-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 1.25rem;
    }

    .item-card {
        display: flex;
        flex-direction: column;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
        backdrop-filter: blur(30px) saturate(120%);
        -webkit-backdrop-filter: blur(30px) saturate(120%);
        border-radius: 16px;
        padding: 1.5rem;
        border: none;
        box-shadow:
            0 4px 16px rgba(0, 0, 0, 0.04),
            inset 0 1px 0 rgba(255, 255, 255, 0.3),
            0 0 0 1px rgba(255, 255, 255, 0.15);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .item-card:hover {
        transform: translateY(-2px);
        box-shadow:
            0 8px 24px rgba(0, 0, 0, 0.06),
            inset 0 1px 0 rgba(255, 255, 255, 0.4),
            0 0 0 1px rgba(255, 255, 255, 0.2);
    }

    .item-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 0.75rem;
    }

    .item-title {
        font-size: 1rem;
        font-weight: 600;
        color: #2c3e50;
    }

    .item-status {
        padding: 0.5rem 1rem;
        border-radius: 8px;
        font-size: 0.85rem;
        font-weight: 600;
        margin-bottom: 0.75rem;
        text-align: left;
    }

    .status-allowed {
        background: linear-gradient(135deg, rgba(102, 187, 106, 0.2) 0%, rgba(76, 175, 80, 0.15) 100%);
        color: #2e7d32;
    }

    .status-not-allowed {
        background: linear-gradient(135deg, rgba(239, 83, 80, 0.2) 0%, rgba(229, 57, 53, 0.15) 100%);
        color: #c62828;
    }

    .status-requires-permission {
        background: linear-gradient(135deg, rgba(255, 167, 38, 0.2) 0%, rgba(255, 152, 0, 0.15) 100%);
        color: #e65100;
    }

    .status-association {
        background: linear-gradient(135deg, rgba(66, 165, 245, 0.2) 0%, rgba(33, 150, 243, 0.15) 100%);
        color: #1565c0;
    }

    .item-explanation {
        margin: 0;
        color: #495057;
        font-size: 0.9rem;
        line-height: 1.6;
        text-align: left;
    }

    .item-details {
        margin-top: 0.75rem;
        padding-top: 0.75rem;
        border-top: 1px solid rgba(0, 0, 0, 0.08);
        color: #6c757d;
        font-size: 0.85rem;
        line-height: 1.5;
        text-align: left;
    }

    .item-details strong {
        color: #495057;
        font-weight: 600;
    }

    @media (max-width: 992px) {
        .stadgar-content {
            padding: 1.5rem;
        }

        .items-grid {
            grid-template-columns: 1fr;
        }

        .economy-grid {
            grid-template-columns: 1fr;
        }
    }
</style>
